import { joinAddressDisplayParts, formatCountryForAddressDisplay } from './addressJoinParts';

/** Malaysia country TOTAL_CODE (B15 / CM). */
export const MY_MALAYSIA_COUNTRY_CODE = 'B15135';

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const B15_COUNTRY_CODE_LIKE = /^B15\d{3}$/i;

function isInternalGuidLike(value: string): boolean {
    return UUID_LIKE.test(value.trim());
}

/** B15 국가 TOTAL_CODE (CODE_NAME으로 변환돼야 하는 값 — 원본 코드는 화면에 노출 금지) */
function isB15CountryCodeLike(value: unknown): boolean {
    return B15_COUNTRY_CODE_LIKE.test(String(value ?? '').trim());
}

export function isMyLocationGuidLike(value: unknown): boolean {
    return isInternalGuidLike(String(value ?? '').trim());
}

/** SPC/레거시 — 주소 필드에 들어오는 무효 placeholder */
export function isMyLocationPlaceholderValue(raw: unknown): boolean {
    const s = String(raw ?? '').trim();
    if (!s) return true;
    return /^(NA|N\/A|N\.A\.|NONE|null|-)$/i.test(s);
}

export function sanitizeMyLocationValueForForm(raw: unknown): string {
    if (isMyLocationPlaceholderValue(raw)) return '';
    return String(raw ?? '').trim();
}

/**
 * 폼 v-model용 — *Code(GUID) 우선, NA 제거, options로 TOTAL_CODE 통일.
 */
export function resolveMyLocationFieldForForm(
    primary: unknown,
    codeAlt: unknown,
    options: MyLocationOption[] = [],
): string {
    const fromCode = sanitizeMyLocationValueForForm(codeAlt);
    if (fromCode) {
        return resolveTotalCodeFromField(fromCode, options) || fromCode;
    }
    const fromPrimary = sanitizeMyLocationValueForForm(primary);
    if (fromPrimary) {
        return resolveTotalCodeFromField(fromPrimary, options) || fromPrimary;
    }
    return '';
}

export function resolveTotalCodeFromField(
    raw: unknown,
    options: Array<{ TOTAL_CODE: string; CODE_NAME: string }>
): string {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    if (options.some((o) => o.TOTAL_CODE === v)) return v;
    const byName = options.find(
        (o) =>
            o.CODE_NAME === v ||
            (o.CODE_NAME && o.CODE_NAME.toUpperCase() === v.toUpperCase())
    );
    return byName?.TOTAL_CODE || v;
}

/** MY: state / city / postcode — Malaysia only (empty country = hide). */
export function shouldShowMyLocationFields(
    country: unknown,
    countryOptions: Array<{ TOTAL_CODE: string; CODE_NAME: string }> = []
): boolean {
    const raw = country != null ? String(country).trim() : '';
    if (!raw) return false;
    if (raw === MY_MALAYSIA_COUNTRY_CODE) return true;
    const code = resolveTotalCodeFromField(country, countryOptions);
    return code === MY_MALAYSIA_COUNTRY_CODE;
}

/** Add 직후 country 비어 있을 때 State/City/Postcode 3개 드롭다운 기본 표시 */
export function shouldShowMyLocationFieldsWithAddDefault(
    country: unknown,
    countryOptions: Array<{ TOTAL_CODE: string; CODE_NAME: string }> = [],
    showDefaultsOnAdd = false
): boolean {
    if (showDefaultsOnAdd && !String(country ?? '').trim()) return true;
    return shouldShowMyLocationFields(country, countryOptions);
}

/** MY State 표시명/GUID 여부 — Country 누락 시 Malaysia 추론용 */
export function looksLikeMalaysiaState(
    stateRaw: unknown,
    stateOpts: MyLocationOption[] = [],
): boolean {
    const v = sanitizeMyLocationValueForForm(stateRaw);
    if (!v) return false;
    if (
        stateOpts.some(
            (o) =>
                o.TOTAL_CODE === v ||
                o.CODE_NAME === v ||
                (o.CODE_NAME && o.CODE_NAME.toUpperCase() === v.toUpperCase()),
        )
    ) {
        return true;
    }
    if (isInternalGuidLike(v)) return true;
    const n = v
        .toUpperCase()
        .replace(/\./g, '')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return (
        /^(WP |W P |JOHOR|KEDAH|KELANTAN|MELAKA|NEGERI SEMBILAN|PAHANG|PERAK|PERLIS|PULAU PINANG|SABAH|SARAWAK|SELANGOR|TERENGGANU)/.test(
            n,
        ) ||
        n.includes('KUALA LUMPUR') ||
        n.includes('LABUAN') ||
        n.includes('PUTRAJAYA')
    );
}

/**
 * Country가 비어 있고 State(또는 City+Postcode)가 MY이면 Malaysia(B15135)를 채움.
 * Edit 시 State/City/Postcode 미표시·hydrate 스킵 방지.
 */
export function ensureMalaysiaCountryWhenMyLocationPresent(
    address: Record<string, unknown> | null | undefined,
    opts: {
        countryOpts?: MyLocationOption[];
        stateOpts?: MyLocationOption[];
    } = {},
): boolean {
    if (!address || typeof address !== 'object') return false;
    const countryOpts = opts.countryOpts || [];
    const stateOpts = opts.stateOpts || [];

    const existingRaw =
        sanitizeMyLocationValueForForm(address.country) ||
        sanitizeMyLocationValueForForm(address.countryCode);
    if (existingRaw) {
        const resolved =
            resolveCountryTotalCode(existingRaw, countryOpts) ||
            (/^malaysia$/i.test(existingRaw) ? MY_MALAYSIA_COUNTRY_CODE : '');
        if (resolved === MY_MALAYSIA_COUNTRY_CODE || /^malaysia$/i.test(existingRaw)) {
            address.country = MY_MALAYSIA_COUNTRY_CODE;
            address.countryCode = MY_MALAYSIA_COUNTRY_CODE;
            return true;
        }
        // 다른 국가가 명시되어 있으면 덮어쓰지 않음
        return resolved === MY_MALAYSIA_COUNTRY_CODE;
    }

    const stateRaw =
        sanitizeMyLocationValueForForm(address.state) ||
        sanitizeMyLocationValueForForm(address.stateCode);
    const cityRaw =
        sanitizeMyLocationValueForForm(address.city) ||
        sanitizeMyLocationValueForForm(address.cityCode);
    const postcodeRaw = sanitizeMyLocationValueForForm(
        address.post_code ?? address.postcode ?? address.postalCode ?? address.postcodeCode,
    );

    const myState = looksLikeMalaysiaState(stateRaw, stateOpts);
    if (!myState && !(stateRaw && (cityRaw || postcodeRaw)) && !(cityRaw && postcodeRaw)) {
        return false;
    }
    // MY Register/Corporate: State가 MY이거나 State+하위필드가 있으면 Country 누락으로 간주
    if (!myState && !stateRaw) return false;

    address.country = MY_MALAYSIA_COUNTRY_CODE;
    address.countryCode = MY_MALAYSIA_COUNTRY_CODE;
    return true;
}

export function clearMyLocationFieldsOnAddress(addr: Record<string, unknown> | null | undefined): void {
    if (!addr || typeof addr !== 'object') return;
    addr.state = '';
    addr.city = '';
    addr.post_code = '';
    addr.postcode = '';
    addr.postalCode = '';
    addr.stateCode = '';
    addr.cityCode = '';
    addr.postcodeCode = '';
}

/** SPC/레거시 NA 등 — 하위 location 필드만 placeholder면 빈 값으로 */
export function clearMyLocationPlaceholdersOnAddress(
    addr: Record<string, unknown> | null | undefined,
): void {
    if (!addr || typeof addr !== 'object') return;
    for (const key of [
        'state',
        'city',
        'post_code',
        'postcode',
        'postalCode',
        'stateCode',
        'cityCode',
        'postcodeCode',
    ]) {
        if (isMyLocationPlaceholderValue(addr[key])) {
            addr[key] = '';
        }
    }
}

/**
 * Postcode v-model(TOTAL_CODE) — post_code 표시값(81300) 우선 역조회, orphan GUID는 마지막.
 */
export function resolvePostcodeTotalCodeForForm(
    primary: unknown,
    codeAlt: unknown,
    options: MyLocationOption[] = [],
): string {
    const resolveKnown = (raw: unknown): string => {
        const v = sanitizeMyLocationValueForForm(raw);
        if (!v) return '';
        if (options.some((o) => o.TOTAL_CODE === v)) return v;
        const byName = options.find(
            (o) =>
                o.CODE_NAME === v ||
                (o.CODE_NAME && o.CODE_NAME.toUpperCase() === v.toUpperCase()),
        );
        if (byName) return byName.TOTAL_CODE;
        const resolved = resolveTotalCodeFromField(v, options);
        if (resolved && options.some((o) => o.TOTAL_CODE === resolved)) return resolved;
        return '';
    };

    const fromPrimary = resolveKnown(primary);
    if (fromPrimary) return fromPrimary;

    const fromCode = resolveKnown(codeAlt);
    if (fromCode) return fromCode;

    const codeAltRaw = sanitizeMyLocationValueForForm(codeAlt);
    if (codeAltRaw && isInternalGuidLike(codeAltRaw)) return codeAltRaw;
    const primaryRaw = sanitizeMyLocationValueForForm(primary);
    if (primaryRaw && isInternalGuidLike(primaryRaw)) return primaryRaw;

    return codeAltRaw || primaryRaw || '';
}

/** Postcode 표시용 숫자(CODE_NAME) — options/merged에서 TOTAL_CODE 역조회 */
export function resolvePostcodeDisplayNameFromOptions(
    raw: unknown,
    options: MyLocationOption[] = [],
): string {
    const value = sanitizeMyLocationValueForForm(raw);
    if (!value) return '';
    const byTotal = options.find((o) => o.TOTAL_CODE === value);
    if (byTotal?.CODE_NAME) return byTotal.CODE_NAME;
    if (options.some((o) => o.CODE_NAME === value)) return value;
    return value;
}

/** Country TOTAL_CODE 비교 (옵션·CODE_NAME·빈 값 통일) */
export function resolveCountryTotalCode(
    raw: unknown,
    countryOptions: MyLocationOption[] = []
): string {
    return (
        resolveTotalCodeFromField(raw, countryOptions) || String(raw ?? '').trim()
    );
}

export function countriesDiffer(
    previous: unknown,
    next: unknown,
    countryOptions: MyLocationOption[] = []
): boolean {
    return (
        resolveCountryTotalCode(previous, countryOptions) !==
        resolveCountryTotalCode(next, countryOptions)
    );
}

/**
 * Country가 실제로 바뀌면 State/City/Postcode(+ code) 초기화.
 * Malaysia 여부와 무관하게 이전 국가 하위값이 저장·역동기화되지 않게 함.
 */
export function applyCountryChangeClearsSubLocation(
    addr: Record<string, unknown> | null | undefined,
    previousCountry: unknown,
    newCountry: unknown,
    countryOptions: MyLocationOption[] = []
): boolean {
    if (!addr || typeof addr !== 'object') return false;
    if (!countriesDiffer(previousCountry, newCountry, countryOptions)) return false;
    clearMyLocationFieldsOnAddress(addr);
    return true;
}

export type BuildMyLocalAddressInput = {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    country?: string;
    state?: string;
    city?: string;
    /** 폼에 바인딩된 postcode TOTAL_CODE */
    postcode?: string;
    countryOpts?: MyLocationOption[];
    stateOpts?: MyLocationOption[];
    cityOpts?: MyLocationOption[];
    postcodeOpts?: MyLocationOption[];
    /** IsSync 등 보존할 추가 필드만 (spread로 옛 stateCode 유입 방지) */
    extra?: Record<string, unknown>;
};

export type MyLocalAddressRecord = {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    country: string;
    state: string;
    city: string;
    postcode: string;
    post_code: string;
    countryCode: string;
    stateCode: string;
    cityCode: string;
    postcodeCode: string;
    IsSync?: unknown;
    isSync?: unknown;
};

/** Residential localAddress emit — stale *Code 키 없이 명시 필드만 */
export function buildMyLocalAddressRecordFromForm(
    input: BuildMyLocalAddressInput
): MyLocalAddressRecord {
    const countryOpts = input.countryOpts || [];
    const stateOpts = input.stateOpts || [];
    const cityOpts = input.cityOpts || [];
    const postcodeOpts = input.postcodeOpts || [];

    const countryStored = normalizeMyLocationFieldForPersist(
        input.country,
        countryOpts
    );
    const isMalaysia = shouldShowMyLocationFields(countryStored, countryOpts);

    const stateCode = isMalaysia
        ? normalizeMyLocationFieldForPersist(input.state, stateOpts)
        : '';
    const cityCode = isMalaysia
        ? normalizeMyLocationFieldForPersist(input.city, cityOpts)
        : '';
    const postcodeCode = isMalaysia ? String(input.postcode || '').trim() : '';
    let postcodeName = '';
    if (postcodeCode && postcodeOpts.length > 0) {
        const match = postcodeOpts.find((opt) => opt.TOTAL_CODE === postcodeCode);
        postcodeName = match?.CODE_NAME || '';
    }
    if (!postcodeName && postcodeCode && !isInternalGuidLike(postcodeCode)) {
        postcodeName = postcodeCode;
    }

    const record: MyLocalAddressRecord = {
        addressLine1: String(input.addressLine1 || '').trim(),
        addressLine2: String(input.addressLine2 || '').trim(),
        addressLine3: String(input.addressLine3 || '').trim(),
        country: countryStored,
        /** 폼 v-model·autocomplete — TOTAL_CODE(GUID) 유지 */
        state: stateCode,
        city: cityCode,
        postcode: postcodeCode,
        /** L_CONTACT_ADDRESS 저장용 표시값 — GUID는 post_code에 넣지 않음 */
        post_code: postcodeName,
        countryCode: countryStored,
        stateCode,
        cityCode,
        postcodeCode,
    };

    const extra = input.extra || {};
    if (extra.IsSync !== undefined) record.IsSync = extra.IsSync;
    if (extra.isSync !== undefined) record.isSync = extra.isSync;

    return record;
}

/** CONTACT_JSON service/business 저장 — 비-Malaysia면 MY 하위 location 제거 */
export function sanitizeMyJsonLocationRowForPersist(
    row: Record<string, unknown>,
    countryOpts: MyLocationOption[] = []
): Record<string, unknown> {
    const o = { ...row };
    const countryStored = normalizeMyLocationFieldForPersist(o.country, countryOpts);
    o.country = countryStored;
    if (shouldShowMyLocationFields(countryStored, countryOpts)) {
        return o;
    }
    o.state = '';
    o.city = '';
    o.post_code = '';
    delete o.postcode;
    delete o.postalCode;
    delete o.stateCode;
    delete o.cityCode;
    delete o.postcodeCode;
    return o;
}

export type MyLocationOption = { TOTAL_CODE: string; CODE_NAME: string };

/** v-autocomplete v-model:search — 선택된 TOTAL_CODE → 표시용 CODE_NAME */
export function countryCodeToSearchLabel(
    country: unknown,
    options: MyLocationOption[] = []
): string {
    const raw = String(country ?? '').trim();
    if (!raw) return '';
    if (options.some((o) => o.TOTAL_CODE === raw)) {
        return options.find((o) => o.TOTAL_CODE === raw)!.CODE_NAME;
    }
    const code = resolveTotalCodeFromField(country, options);
    if (!code) return raw;
    return options.find((o) => o.TOTAL_CODE === code)?.CODE_NAME || raw;
}

/** 폼·저장: CODE_NAME / 레거시 값 → TOTAL_CODE (없으면 원본 유지). */
export function normalizeMyLocationFieldForPersist(
    raw: unknown,
    options: MyLocationOption[] = []
): string {
    return resolveTotalCodeFromField(raw, options) || String(raw ?? '').trim();
}

/** TOTAL_CODE(GUID) → 표시명. L_CONTACT_ADDRESS 저장용 */
export function myLocationCodeToDisplayName(
    code: unknown,
    options: MyLocationOption[] = [],
): string {
    const value = String(code ?? '').trim();
    if (!value) return '';
    if (value === MY_MALAYSIA_COUNTRY_CODE) {
        const my = options.find((opt) => opt.TOTAL_CODE === MY_MALAYSIA_COUNTRY_CODE);
        return my?.CODE_NAME || 'Malaysia';
    }
    const found = options.find((opt) => opt.TOTAL_CODE === value);
    if (found?.CODE_NAME) return found.CODE_NAME;
    const byName = options.find(
        (o) =>
            o.CODE_NAME === value
            || (o.CODE_NAME && o.CODE_NAME.toUpperCase() === value.toUpperCase()),
    );
    if (byName?.CODE_NAME) return byName.CODE_NAME;
    if (isInternalGuidLike(value)) return '';
    // 옵션에서 못 찾은 B15 국가코드는 원본 코드를 노출하지 않음
    if (isB15CountryCodeLike(value)) return '';
    return value;
}

/** JSON 주소 행 로드 시 country/state/city를 autocomplete용 TOTAL_CODE로 통일. */
export function normalizeMyLocationRowForForm(
    row: Record<string, unknown> | null | undefined,
    ctx: {
        countryOpts?: MyLocationOption[];
        stateOpts?: MyLocationOption[];
        cityOpts?: MyLocationOption[];
    } = {}
): Record<string, unknown> {
    const o = { ...(row || {}) };
    // Country 누락 + MY State → Malaysia 먼저 채움 (State/City 콤보 표시용)
    ensureMalaysiaCountryWhenMyLocationPresent(o, {
        countryOpts: ctx.countryOpts,
        stateOpts: ctx.stateOpts,
    });
    if (ctx.countryOpts?.length) {
        o.country = normalizeMyLocationFieldForPersist(o.country, ctx.countryOpts);
    }
    if (ctx.stateOpts?.length) {
        o.state = normalizeMyLocationFieldForPersist(o.state, ctx.stateOpts);
    }
    if (ctx.cityOpts?.length) {
        o.city = normalizeMyLocationFieldForPersist(o.city, ctx.cityOpts);
    }
    if (o.country === MY_MALAYSIA_COUNTRY_CODE) {
        o.countryCode = MY_MALAYSIA_COUNTRY_CODE;
    }
    return o;
}

function getDisplayCodeName(rawValue: unknown, options: MyLocationOption[] = []): string {
    const value = String(rawValue ?? '').trim();
    if (!value) return '';
    if (value === MY_MALAYSIA_COUNTRY_CODE) {
        const my = options.find((opt) => opt.TOTAL_CODE === MY_MALAYSIA_COUNTRY_CODE);
        return my?.CODE_NAME || 'Malaysia';
    }
    const found = options.find((opt) => opt.TOTAL_CODE === value);
    if (found?.CODE_NAME) return found.CODE_NAME;
    const byName = options.find(
        (o) =>
            o.CODE_NAME === value ||
            (o.CODE_NAME && o.CODE_NAME.toUpperCase() === value.toUpperCase()),
    );
    if (byName?.CODE_NAME) return byName.CODE_NAME;
    if (isInternalGuidLike(value)) return '';
    // 옵션에서 못 찾은 B15 국가코드는 원본 코드를 노출하지 않음
    if (isB15CountryCodeLike(value)) return '';
    return value;
}

function resolveLocationDisplayField(
    primary: unknown,
    codeAlt: unknown,
    options: MyLocationOption[] = [],
): string {
    const fromPrimary = getDisplayCodeName(primary, options);
    if (fromPrimary) return fromPrimary;
    const fromCode = getDisplayCodeName(codeAlt, options);
    if (fromCode) return fromCode;
    const raw = String(primary ?? codeAlt ?? '').trim();
    if (!raw || isInternalGuidLike(raw)) return '';
    if (/^(NA|N\/A|N\.A\.|NONE|null|-)$/i.test(raw)) return '';
    // B15 국가코드가 옵션 미로드 등으로 남아도 원본 코드는 노출하지 않음
    if (isB15CountryCodeLike(raw)) return '';
    return raw;
}

/** Detail UI 최종 표시 — 단어별 첫 글자 대문자 (숫자·우편번호-only 구간은 유지) */
function toTitleCaseWord(word: string): string {
    const w = word.trim();
    if (!w) return '';
    if (/^\d+$/.test(w)) return w;
    // Keep dotted acronyms uppercase (e.g. W.P., U.S.A.)
    if (/^(?:[A-Za-z]\.){2,}[A-Za-z]?\.?$/.test(w)) return w.toUpperCase();
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/** @deprecated 주소 표시에는 preserveLiteralDisplayPart 사용 — Title Case 변환만 필요할 때만 사용 */
export function toTitleCaseDisplay(str: unknown): string {
    const t = String(str ?? '').trim();
    if (!t) return '';
    if (/^[\d\s\-]+$/.test(t)) return t.replace(/\s+/g, ' ');
    return t
        .split(/(\s+|\/|,|-)/)
        .map((part) => {
            if (!part || /^[\s/,\-]+$/.test(part)) return part;
            return toTitleCaseWord(part);
        })
        .join('');
}

/** 주소 조립 표시용 — 원문 유지(trim·연속 공백만 정리, Title Case / &→and 변환 없음) */
export function preserveLiteralDisplayPart(value: unknown): string {
    const s = String(value ?? '').trim();
    if (!s || isMyLocationPlaceholderValue(s)) return '';
    return s.replace(/\s+/g, ' ');
}

export type MyAddressDisplayContext = {
    countryOpts?: MyLocationOption[];
    stateOpts?: MyLocationOption[];
    cityOpts?: MyLocationOption[];
    postcodeOpts?: MyLocationOption[];
};

/** MY Corporate branch — Alias 칩용 (ALIAS 컬럼) */
export function getMyAddressAlias(
    address: Record<string, unknown> | null | undefined,
): string {
    return String(address?.alias ?? address?.ALIAS ?? '').trim();
}

/** 주소 본문만 (Alias 제외 — 칩으로 따로 표시) */
export function formatMyAddressRowBodyForDisplay(
    address: Record<string, unknown> | null | undefined,
    ctx: MyAddressDisplayContext = {},
): string {
    const a = { ...(address || {}) };
    const countryOpts = ctx.countryOpts || [];
    const stateOpts = ctx.stateOpts || [];
    const cityOpts = ctx.cityOpts || [];
    const postcodeOpts = ctx.postcodeOpts || [];

    // Country 누락인데 MY State면 표시줄 끝에 Malaysia 붙임
    ensureMalaysiaCountryWhenMyLocationPresent(a, { countryOpts, stateOpts });

    const countryDisplay = formatCountryForAddressDisplay(
        preserveLiteralDisplayPart(
            resolveLocationDisplayField(a.country, a.countryCode, countryOpts) ||
                countryCodeToSearchLabel(a.country, countryOpts)
        )
    );

    // Malaysia가 아니면 Address Line 1~3 + Country만 (Postcode/City/State 잔여값 숨김)
    const showMyLoc = shouldShowMyLocationFields(
        a.countryCode || a.country,
        countryOpts,
    );

    if (!showMyLoc) {
        return joinAddressDisplayParts([
            preserveLiteralDisplayPart(a.street01 || a.addressLine1),
            preserveLiteralDisplayPart(a.street02 || a.addressLine2),
            preserveLiteralDisplayPart(a.street03 || a.addressLine3),
            countryDisplay,
        ]);
    }

    const stateDisplay = preserveLiteralDisplayPart(
        resolveLocationDisplayField(a.state, a.stateCode, stateOpts)
    );
    const cityDisplay = preserveLiteralDisplayPart(
        resolveLocationDisplayField(a.city, a.cityCode, cityOpts)
    );

    const pcRaw = a.post_code ?? a.postcode ?? a.postalCode ?? a.postcodeCode;
    const postcodeDisplay = preserveLiteralDisplayPart(
        resolveLocationDisplayField(pcRaw, a.postcodeCode, postcodeOpts)
    );

    return joinAddressDisplayParts([
        preserveLiteralDisplayPart(a.street01 || a.addressLine1),
        preserveLiteralDisplayPart(a.street02 || a.addressLine2),
        preserveLiteralDisplayPart(a.street03 || a.addressLine3),
        postcodeDisplay,
        cityDisplay,
        stateDisplay,
        countryDisplay,
    ]);
}

/** MY 주소 한 줄 표시 — TOTAL_CODE/GUID는 CODE_NAME·숫자 우편번호로, 내부 GUID는 제외 */
export function formatMyAddressRowForDisplay(
    address: Record<string, unknown> | null | undefined,
    ctx: MyAddressDisplayContext = {},
): string {
    const aliasLabel = getMyAddressAlias(address);
    const base = formatMyAddressRowBodyForDisplay(address, ctx);

    if (aliasLabel && base) return `${aliasLabel} — ${base}`;
    if (aliasLabel) return aliasLabel;

    return base;
}

/** MY Individual Residential 요약 — Corporate Registered와 동일 표시 규칙 */
export type MyResidentialFormSnapshot = {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
};

/** 요약 표시용 — API/컬럼 이름 우선, TOTAL_CODE(GUID)는 resolve 전용으로만 */
function pickResidentialSummaryLocationFields(
    la: Record<string, unknown>,
    myData: MyResidentialFormSnapshot
): Record<string, unknown> {
    const countryName = String(la.country ?? '').trim();
    const stateName = String(la.state ?? '').trim();
    const cityName = String(la.city ?? '').trim();
    const postcodeName = String(
        la.post_code ?? la.postcode ?? la.postalCode ?? ''
    ).trim();

    return {
        country: countryName,
        countryCode: la.countryCode ?? myData.country,
        state: stateName,
        stateCode: la.stateCode,
        city: cityName,
        cityCode: la.cityCode,
        post_code: postcodeName,
        postcode: postcodeName,
        postcodeCode: la.postcodeCode,
    };
}

export function formatMyResidentialSummaryForDisplay(
    myData: MyResidentialFormSnapshot,
    localAddress: Record<string, unknown> | null | undefined,
    ctx: MyAddressDisplayContext = {}
): string {
    const la = localAddress || {};
    const loc = pickResidentialSummaryLocationFields(la, myData);
    return formatMyAddressRowBodyForDisplay(
        {
            addressLine1: myData.addressLine1,
            addressLine2: myData.addressLine2,
            addressLine3: myData.addressLine3,
            street01: myData.addressLine1,
            street02: myData.addressLine2,
            street03: myData.addressLine3,
            ...loc,
        },
        ctx
    );
}

const EMPTY_ENTITY_CD = '00000000-0000-0000-0000-000000000000';

/** MY Corporate branch: L_CONTACT_BRANCH_MAP 에 ENTITY_CD 매핑됨 */
export function isMyCorporateBranchAddressEntityMapped(
    addr: Record<string, unknown> | null | undefined,
): boolean {
    if (!addr || typeof addr !== 'object') return false;
    const cd = String(addr.entity_cd ?? addr.ENTITY_CD ?? '').trim();
    if (cd && cd !== EMPTY_ENTITY_CD) return true;
    const flag = addr.has_entity_map ?? addr.hasEntityMap ?? addr.IS_ENTITY_MAPPED;
    return flag === true || flag === 1 || flag === '1';
}

/** MY 주소 행 — Delete 버튼 표시 (편집 가능 시 항상 표시) */
export function showDeleteMyCorporateBranchAddress(
    _addr?: Record<string, unknown> | null | undefined,
    _options?: { useBranchAddresses?: boolean; contactListFlatAddressMode?: boolean },
): boolean {
    return true;
}
